import { Settings, fetchSettings } from '../api/settings';
import { getCategories, getProducts, Category, Product } from '../api/products';

interface PreloadedData {
    settings: Settings | null;
    categories: Category[];
    featuredProducts: Product[];
}

class DataPreloader {
    private static instance: DataPreloader;
    private preloadedData: PreloadedData | null = null;
    private preloadPromise: Promise<PreloadedData> | null = null;
    private isPreloading = false;

    private constructor() { }

    static getInstance(): DataPreloader {
        if (!DataPreloader.instance) {
            DataPreloader.instance = new DataPreloader();
        }
        return DataPreloader.instance;
    }

    /**
     * Start preloading critical data in parallel
     * This should be called as early as possible in the app lifecycle
     */
    async preload(): Promise<PreloadedData> {
        // If already preloaded, return cached data
        if (this.preloadedData) {
            return this.preloadedData;
        }

        // If currently preloading, return the existing promise
        if (this.preloadPromise) {
            return this.preloadPromise;
        }

        // Start preloading
        this.isPreloading = true;
        this.preloadPromise = this.fetchAllData();

        try {
            this.preloadedData = await this.preloadPromise;
            return this.preloadedData;
        } finally {
            this.isPreloading = false;
        }
    }

    /**
     * Fetch all critical data in parallel
     */
    private async fetchAllData(): Promise<PreloadedData> {
        console.log('[DataPreloader] Starting parallel data fetch...');
        const startTime = performance.now();

        try {
            // Fetch all data in parallel
            const [settings, categories, products] = await Promise.all([
                fetchSettings().catch(err => {
                    console.error('[DataPreloader] Failed to fetch settings:', err);
                    return null;
                }),
                getCategories().catch(err => {
                    console.error('[DataPreloader] Failed to fetch categories:', err);
                    return [];
                }),
                getProducts({ limit: 10 }).catch(err => {
                    console.error('[DataPreloader] Failed to fetch products:', err);
                    return [];
                })
            ]);

            const endTime = performance.now();
            console.log(`[DataPreloader] Data loaded in ${(endTime - startTime).toFixed(2)}ms`);

            return {
                settings,
                categories,
                featuredProducts: products
            };
        } catch (error) {
            console.error('[DataPreloader] Critical error during preload:', error);
            // Return empty data structure on critical failure
            return {
                settings: null,
                categories: [],
                featuredProducts: []
            };
        }
    }

    /**
     * Get preloaded data (returns null if not yet loaded)
     */
    getPreloadedData(): PreloadedData | null {
        return this.preloadedData;
    }

    /**
     * Check if data is currently being preloaded
     */
    isLoading(): boolean {
        return this.isPreloading;
    }

    /**
     * Clear cached data (useful for forcing refresh)
     */
    clearCache(): void {
        this.preloadedData = null;
        this.preloadPromise = null;
    }
}

// Export singleton instance
export const dataPreloader = DataPreloader.getInstance();
export type { PreloadedData };
